<template>
  <el-scrollbar
    ref="scrollbar"
    :key="menuId"
    tag="ul"
    role="menu"
    :class="[ns.b(), ns.is('virtual', isVirtual)]"
    :wrap-class="ns.e('wrap')"
    :view-class="[ns.e('list'), ns.is('empty', isEmpty)]"
    @scroll="handleScroll"
    @mousemove="handleMouseMove"
    @mouseleave="clearHoverZone"
  >
    <li
      v-if="isVirtual"
      :class="ns.e('phantom')"
      :style="phantomStyle"
      aria-hidden="true"
    />
    <el-cascader-node
      v-for="item in displayNodes"
      :key="item.node.uid"
      :node="item.node"
      :menu-id="menuId"
      :style="item.style"
      @expand="handleExpand"
    />
    <div v-if="isLoading" :class="ns.e('empty-text')">
      <el-icon size="14" :class="ns.is('loading')">
        <loading />
      </el-icon>
      {{ t('el.cascader.loading') }}
    </div>
    <div v-else-if="isEmpty" :class="ns.e('empty-text')">
      {{ t('el.cascader.noData') }}
    </div>
    <!-- eslint-disable-next-line vue/html-self-closing -->
    <svg
      v-else-if="panel?.isHoverMenu && !isVirtual"
      ref="hoverZone"
      :class="ns.e('hover-zone')"
    ></svg>
  </el-scrollbar>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  inject,
  nextTick,
  ref,
  watch,
} from 'vue'
import ElScrollbar from '@element-plus/components/scrollbar'
import { useLocale, useNamespace } from '@element-plus/hooks'
import { focusNode, generateId } from '@element-plus/utils'
import { Loading } from '@element-plus/icons-vue'
import ElIcon from '@element-plus/components/icon'
import ElCascaderNode from './node.vue'
import {
  CASCADER_MENU_HEIGHT,
  CASCADER_MENU_PADDING,
  CASCADER_NODE_HEIGHT,
  CASCADER_VIRTUAL_OVERSCAN,
  CASCADER_VIRTUAL_THRESHOLD,
} from './menu-layout'
import { CASCADER_PANEL_INJECTION_KEY } from './types'

import type { CSSProperties, PropType } from 'vue'
import type { default as CascaderNode } from './node'
import type { Nullable } from '@element-plus/utils'

export default defineComponent({
  name: 'ElCascaderMenu',

  components: {
    Loading,
    ElIcon,
    ElScrollbar,
    ElCascaderNode,
  },

  props: {
    nodes: {
      type: Array as PropType<CascaderNode[]>,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },

  setup(props) {
    const instance = getCurrentInstance()!
    const ns = useNamespace('cascader-menu')

    const { t } = useLocale()
    const id = generateId()
    let activeNode: Nullable<HTMLElement> = null
    let hoverTimer: Nullable<number> = null

    const panel = inject(CASCADER_PANEL_INJECTION_KEY)!

    const hoverZone = ref<null | SVGSVGElement>(null)
    const scrollbar = ref<InstanceType<typeof ElScrollbar> | null>(null)
    const scrollOffset = ref(0)

    const isEmpty = computed(() => !props.nodes.length)
    const isLoading = computed(() => !panel.initialLoaded)
    const menuId = computed(() => `cascader-menu-${id}-${props.index}`)
    const isVirtual = computed(
      () => props.nodes.length > CASCADER_VIRTUAL_THRESHOLD
    )
    const phantomStyle = computed(() => ({
      height: `${props.nodes.length * CASCADER_NODE_HEIGHT}px`,
    }))
    const displayNodes = computed(() => {
      const { nodes } = props
      if (!isVirtual.value) {
        return nodes.map((node) => ({ node, style: undefined }))
      }

      const start = Math.max(
        0,
        Math.floor(
          (scrollOffset.value - CASCADER_MENU_PADDING) / CASCADER_NODE_HEIGHT
        ) - CASCADER_VIRTUAL_OVERSCAN
      )
      const visibleCount =
        Math.ceil(CASCADER_MENU_HEIGHT / CASCADER_NODE_HEIGHT) +
        CASCADER_VIRTUAL_OVERSCAN * 2
      const end = Math.min(nodes.length, start + visibleCount)
      const list: { node: CascaderNode; style: CSSProperties }[] = []

      for (let i = start; i < end; i++) {
        list.push({
          node: nodes[i],
          style: {
            position: 'absolute',
            top: `${CASCADER_MENU_PADDING + i * CASCADER_NODE_HEIGHT}px`,
            left: '0',
            right: '0',
            width: '100%',
          },
        })
      }

      return list
    })

    const isExpandable = (node: CascaderNode) => {
      const { checkStrictly } = panel.config
      return (checkStrictly && !node.isLeaf) || !node.isDisabled
    }

    const queryNodeEl = (node: CascaderNode) => {
      const root = instance.vnode.el as HTMLElement | undefined
      return root?.querySelector(
        `#${menuId.value}-${node.uid}`
      ) as HTMLElement | null
    }

    const handleScroll = ({ scrollTop }: { scrollTop: number }) => {
      scrollOffset.value = scrollTop
    }

    const scrollToIndex = (index: number) => {
      if (index < 0 || index >= props.nodes.length) return

      const itemTop = CASCADER_MENU_PADDING + index * CASCADER_NODE_HEIGHT
      const itemBottom = itemTop + CASCADER_NODE_HEIGHT
      const viewTop = scrollOffset.value
      const viewBottom = viewTop + CASCADER_MENU_HEIGHT
      let nextTop = viewTop

      if (itemTop < viewTop) {
        nextTop = itemTop
      } else if (itemBottom > viewBottom) {
        nextTop = itemBottom - CASCADER_MENU_HEIGHT
      } else if (!isVirtual.value) {
        const el = queryNodeEl(props.nodes[index])
        el?.scrollIntoView({ block: 'nearest' })
        return
      }

      scrollOffset.value = Math.max(0, nextTop)
      scrollbar.value?.setScrollTop(scrollOffset.value)
    }

    const scrollToNode = (node: CascaderNode) => {
      const index = props.nodes.findIndex((item) => item.uid === node.uid)
      if (index > -1) scrollToIndex(index)
    }

    const scrollToActivePath = () => {
      const expanding = panel.expandingNode
      const checked = panel.checkedNodes
      const node = props.nodes.find((item) => {
        const levelIndex = item.level - 1
        if (expanding?.pathNodes[levelIndex]?.uid === item.uid) return true
        return checked.some((cn) => cn.pathNodes[levelIndex]?.uid === item.uid)
      })
      if (node) scrollToNode(node)
    }

    const moveFocus = (el: HTMLElement, distance: number) => {
      const uid = Number(el.id.split('-').pop())
      let index = props.nodes.findIndex((node) => node.uid === uid)
      if (index < 0) return

      index += distance
      while (index >= 0 && index < props.nodes.length) {
        const node = props.nodes[index]
        if (isExpandable(node)) {
          scrollToIndex(index)
          nextTick(() => focusNode(queryNodeEl(node)!))
          return
        }
        index += distance
      }
    }

    const handleExpand = (e: MouseEvent) => {
      activeNode = e.target as HTMLElement
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!panel.isHoverMenu || !activeNode || !hoverZone.value) return

      if (activeNode.contains(e.target as HTMLElement)) {
        clearHoverTimer()

        const el = instance.vnode.el as HTMLElement
        const { left } = el.getBoundingClientRect()
        const { offsetWidth, offsetHeight } = el
        const startX = e.clientX - left
        const top = activeNode.offsetTop
        const bottom = top + activeNode.offsetHeight

        hoverZone.value.innerHTML = `
          <path style="pointer-events: auto;" fill="transparent" d="M${startX} ${top} L${offsetWidth} 0 V${top} Z" />
          <path style="pointer-events: auto;" fill="transparent" d="M${startX} ${bottom} L${offsetWidth} ${offsetHeight} V${bottom} Z" />
        `
      } else if (!hoverTimer) {
        hoverTimer = window.setTimeout(
          clearHoverZone,
          panel.config.hoverThreshold
        )
      }
    }

    const clearHoverTimer = () => {
      if (!hoverTimer) return
      clearTimeout(hoverTimer)
      hoverTimer = null
    }

    const clearHoverZone = () => {
      if (!hoverZone.value) return
      hoverZone.value.innerHTML = ''
      clearHoverTimer()
    }

    watch(
      () => props.nodes[0]?.uid,
      () => {
        scrollOffset.value = 0
        scrollbar.value?.setScrollTop(0)
        nextTick(() => scrollbar.value?.update())
      }
    )

    return {
      ns,
      panel,
      hoverZone,
      scrollbar,
      isEmpty,
      isLoading,
      isVirtual,
      menuId,
      phantomStyle,
      displayNodes,
      t,
      handleScroll,
      handleExpand,
      handleMouseMove,
      clearHoverZone,
      scrollToNode,
      scrollToActivePath,
      moveFocus,
    }
  },
})
</script>
